import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const TZ = "America/Guayaquil";

const CATEGORIES = [
  "Apertura de nevera",
  "Carga / descarga de producto",
  "Corte de energía",
  "Limpieza",
  "Mantenimiento",
  "Puerta abierta",
  "Falla de equipo",
  "Prueba operativa",
  "Otro",
];

function ecuadorNowInput() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());

  const get = (type) => parts.find((part) => part.type === type)?.value || "";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
}

function toInputValue(value) {
  return value ? String(value).replace(" ", "T").slice(0, 16) : "";
}

function displayDateTime(value) {
  if (!value) return "—";
  const raw = String(value);
  const match = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/
  );
  if (!match) return raw;
  const [, year, month, day, hour, minute] = match;
  return `${day}/${month}/${year} ${hour}:${minute}`;
}

export default function ObservationsModal({
  open,
  onClose,
  deviceId,
  sensorName,
  viewAs,
  token,
  apiRoot,
}) {
  const authConfig = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const [category, setCategory] = useState(CATEGORIES[0]);
  const [notes, setNotes] = useState("");
  const [startedAt, setStartedAt] = useState(ecuadorNowInput());
  const [endedAt, setEndedAt] = useState("");
  const [impactMinutes, setImpactMinutes] = useState("20");
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const withAs = (url) =>
    viewAs
      ? `${url}${url.includes("?") ? "&" : "?"}as=${encodeURIComponent(viewAs)}`
      : url;

  const resetForm = () => {
    setEditingId(null);
    setCategory(CATEGORIES[0]);
    setNotes("");
    setStartedAt(ecuadorNowInput());
    setEndedAt("");
    setImpactMinutes("20");
    setMessage("");
  };

  const loadObservations = async () => {
    if (!open || !deviceId) return;
    setLoading(true);
    setMessage("");

    try {
      const url = withAs(
        `${apiRoot}/observations?device_id=${encodeURIComponent(deviceId)}`
      );
      const { data } = await axios.get(url, authConfig);
      setRows(Array.isArray(data?.observations) ? data.observations : []);
    } catch (error) {
      setRows([]);
      setMessage(
        error?.response?.data?.error || "No se pudieron cargar las observaciones."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    resetForm();
    loadObservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deviceId, viewAs]);

  if (!open) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();

    const cleanNotes = String(notes || "").trim();
    const impact = Number(impactMinutes);

    if (!startedAt) {
      setMessage("Debes indicar la fecha y hora inicial.");
      return;
    }
    if (endedAt && endedAt < startedAt) {
      setMessage("La hora final no puede ser anterior a la inicial.");
      return;
    }
    if (cleanNotes.length < 3) {
      setMessage("Escribe una observación de al menos 3 caracteres.");
      return;
    }
    if (!Number.isInteger(impact) || impact < 0 || impact > 1440) {
      setMessage("Los minutos de impacto deben estar entre 0 y 1440.");
      return;
    }

    setSaving(true);
    setMessage("");

    const payload = {
      device_id: deviceId,
      category,
      notes: cleanNotes,
      started_at: startedAt,
      ended_at: endedAt || "",
      impact_minutes: impact,
      ...(viewAs ? { as: viewAs } : {}),
    };

    try {
      if (editingId) {
        await axios.put(
          withAs(`${apiRoot}/observations/${editingId}`),
          payload,
          authConfig
        );
        setMessage("Observación actualizada correctamente.");
      } else {
        await axios.post(
          withAs(`${apiRoot}/observations`),
          payload,
          authConfig
        );
        setMessage("Observación guardada correctamente.");
      }

      setEditingId(null);
      setCategory(CATEGORIES[0]);
      setNotes("");
      setStartedAt(ecuadorNowInput());
      setEndedAt("");
      setImpactMinutes("20");
      await loadObservations();
    } catch (error) {
      setMessage(
        error?.response?.data?.error || "No se pudo guardar la observación."
      );
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setCategory(row.category || "Otro");
    setNotes(row.notes || "");
    setStartedAt(toInputValue(row.started_local));
    setEndedAt(toInputValue(row.ended_local));
    setImpactMinutes(String(row.impact_minutes ?? 20));
    setMessage("");
  };

  const removeObservation = async (id) => {
    const ok = window.confirm("¿Eliminar esta observación?");
    if (!ok) return;

    try {
      await axios.delete(withAs(`${apiRoot}/observations/${id}`), authConfig);
      if (Number(editingId) === Number(id)) resetForm();
      await loadObservations();
    } catch (error) {
      setMessage(
        error?.response?.data?.error || "No se pudo eliminar la observación."
      );
    }
  };

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Observaciones del sensor"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) onClose();
      }}
    >
      <div
        className="modal-card"
        style={{ width: "min(920px, 96vw)", maxHeight: "92vh", overflow: "auto" }}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-head">
          <div>
            <div className="modal-title">Observaciones</div>
            <div className="small" style={{ marginTop: 4 }}>
              {sensorName || deviceId}
            </div>
          </div>
          <button
            type="button"
            className="btn-solid btn-ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cerrar
          </button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="modal-section">
              <div className="section-title">
                {editingId ? "Editar observación" : "Registrar observación"}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
                  gap: 12,
                  marginTop: 12,
                }}
              >
                <label style={{ display: "grid", gap: 6, fontWeight: 700 }}>
                  Tipo
                  <select
                    className="input"
                    value={category}
                    onChange={(event) => setCategory(event.target.value)}
                    disabled={saving}
                  >
                    {CATEGORIES.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </label>

                <label style={{ display: "grid", gap: 6, fontWeight: 700 }}>
                  Inicio
                  <input
                    className="input"
                    type="datetime-local"
                    value={startedAt}
                    onChange={(event) => setStartedAt(event.target.value)}
                    disabled={saving}
                    required
                  />
                </label>

                <label style={{ display: "grid", gap: 6, fontWeight: 700 }}>
                  Fin (opcional)
                  <input
                    className="input"
                    type="datetime-local"
                    min={startedAt || undefined}
                    value={endedAt}
                    onChange={(event) => setEndedAt(event.target.value)}
                    disabled={saving}
                  />
                </label>

                <label style={{ display: "grid", gap: 6, fontWeight: 700 }}>
                  Tiempo (min)
                  <input
                    className="input"
                    type="number"
                    min="0"
                    max="1440"
                    step="1"
                    value={impactMinutes}
                    onChange={(event) => setImpactMinutes(event.target.value)}
                    disabled={saving}
                  />
                </label>
              </div>

              <label
                style={{
                  display: "grid",
                  gap: 6,
                  fontWeight: 700,
                  marginTop: 12,
                }}
              >
                Observación
                <textarea
                  className="input"
                  rows="4"
                  maxLength="2000"
                  placeholder="Ej.: Se abrió la cámara para retiro de producto. La operación terminó a las 09:42."
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  disabled={saving}
                  required
                  style={{ resize: "vertical" }}
                />
              </label>

              <div className="small" style={{ marginTop: 8 }}>
                Minutos adicionales en los que la observación puede relacionarse con cambios del sensor después de finalizar.
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  flexWrap: "wrap",
                  marginTop: 14,
                }}
              >
                {editingId && (
                  <button
                    type="button"
                    className="btn-solid btn-ghost"
                    onClick={resetForm}
                    disabled={saving}
                  >
                    Cancelar edición
                  </button>
                )}
                <button
                  type="submit"
                  className="btn-solid btn-download"
                  disabled={saving}
                >
                  {saving
                    ? "Guardando..."
                    : editingId
                    ? "Actualizar observación"
                    : "Guardar observación"}
                </button>
              </div>

              {message ? (
                <div className="msg" style={{ marginTop: 12 }}>
                  {message}
                </div>
              ) : null}
            </div>
          </form>

          <div className="modal-section" style={{ marginTop: 16 }}>
            <div className="section-title">Historial de observaciones</div>

            {loading ? (
              <div className="small" style={{ marginTop: 12 }}>
                Cargando...
              </div>
            ) : !rows.length ? (
              <div className="small" style={{ marginTop: 12 }}>
                No hay observaciones registradas para este sensor.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
                {rows.map((row) => (
                  <div
                    key={row.id}
                    style={{
                      border: "1px solid #dbe4ee",
                      borderRadius: 12,
                      padding: 12,
                      background: "#fff",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        flexWrap: "wrap",
                      }}
                    >
                      <div>
                        <strong>{row.category || "Observación"}</strong>
                        <div className="small" style={{ marginTop: 4 }}>
                          {displayDateTime(row.started_local)}
                          {row.ended_local
                            ? ` → ${displayDateTime(row.ended_local)}`
                            : ""}
                          {Number(row.impact_minutes) > 0
                            ? ` · impacto +${row.impact_minutes} min`
                            : ""}
                        </div>
                      </div>

                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          type="button"
                          className="btn-solid btn-ghost"
                          onClick={() => startEdit(row)}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          className="btn-solid btn-ghost"
                          onClick={() => removeObservation(row.id)}
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>

                    <div style={{ marginTop: 9, whiteSpace: "pre-wrap" }}>
                      {row.notes}
                    </div>

                    <div className="small" style={{ marginTop: 8 }}>
                      Registrado por: {row.created_by_name || "—"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
